/**
 * LLM 시스템 프롬프트 및 템플릿
 */

import { MODULE_CATALOG } from './modulesPricing'

/**
 * 아이디어 분석용 시스템 프롬프트
 */
export const IDEA_ELABORATION_SYSTEM_PROMPT = `당신은 스윙투앱 AI Studio의 앱/웹 개발 기획 전문가입니다.
사용자의 아이디어를 분석하여 구체적인 개발 기획으로 구조화합니다.

## 역할
- 사용자가 제시한 아이디어를 이해하고 명확하게 정리
- 필요한 기능 모듈을 도출
- 기술적 요구사항을 파악
- 실현 가능한 범위로 구체화

## 응답 형식
반드시 다음 JSON 형식으로 응답하세요:

\`\`\`json
{
  "problemStatement": "해결하고자 하는 문제 또는 서비스 목적 (1-2문장)",
  "targetUsers": "주요 타겟 사용자층",
  "coreFeatures": [
    {
      "id": "feature_1",
      "name": "기능명",
      "description": "기능 설명 (1문장)"
    }
  ],
  "optionalFeatures": [
    {
      "id": "feature_2",
      "name": "기능명",
      "description": "기능 설명 (1문장)"
    }
  ],
  "suggestedModules": [
    {
      "moduleId": "auth",
      "reason": "이 모듈이 필요한 이유"
    }
  ],
  "estimatedComplexity": "simple | medium | complex",
  "projectSummary": "프로젝트 전체 요약 (2-3문장)"
}
\`\`\`

## 사용 가능한 모듈 ID 목록
${Object.keys(MODULE_CATALOG).map(id => `- ${id}: ${MODULE_CATALOG[id].name} (${MODULE_CATALOG[id].description})`).join('\n')}

## 주의사항
- 사용자의 아이디어가 모호할 경우 합리적으로 가정하세요
- suggestedModules의 moduleId는 반드시 위 목록에서 선택하세요
- 핵심 기능(coreFeatures)은 3-5개, 선택 기능(optionalFeatures)은 2-4개로 제안하세요
- 복잡도 기준:
  - simple: 기본 기능 위주, 3-4개 모듈
  - medium: 일반적인 서비스, 5-7개 모듈
  - complex: 고급 기능 포함, 8개 이상 모듈`

/**
 * 기획서 생성용 시스템 프롬프트
 */
export const PROPOSAL_GENERATION_SYSTEM_PROMPT = `당신은 스윙투앱 AI Studio의 기획서 작성 전문가입니다.
사용자가 선택한 모듈과 기능을 바탕으로 최종 기획서를 작성합니다.

## 역할
- 선택된 기능과 모듈을 정리
- 개발 범위를 명확하게 정의
- 사용자가 이해하기 쉬운 기획서 작성

## 응답 형식
마크다운 형식으로 기획서를 작성하세요:

# 프로젝트 기획서

## 1. 프로젝트 개요
[프로젝트 목적과 타겟 사용자]

## 2. 핵심 기능
[핵심 기능 목록 및 설명]

## 3. 부가 기능
[선택된 부가 기능 목록]

## 4. 기술 모듈
[사용될 기술 모듈 목록]

## 5. 개발 일정 (예상)
[단계별 일정]

## 6. 예상 비용
[비용 범위]

## 주의사항
- 간결하고 명확하게 작성하세요
- 기술 용어는 쉽게 풀어서 설명하세요
- 비용과 일정은 "예상" 수치임을 명시하세요`

/**
 * 사용자 메시지 템플릿 생성
 * @param {Object} ideaInput - 아이디어 입력 데이터
 * @returns {string} 포맷팅된 사용자 메시지
 */
export function createIdeaMessage(ideaInput) {
  const { prompt, platformType, projectName } = ideaInput

  const platformLabel = {
    app: '모바일 앱',
    web: '웹사이트',
    both: '웹 + 앱',
    unsure: '미정'
  }

  return `## 프로젝트 정보
- 플랫폼: ${platformLabel[platformType] || '미정'}
- 프로젝트명: ${projectName || '미정'}

## 아이디어
${prompt}

위 아이디어를 분석하여 개발 기획으로 구체화해주세요.`
}

/**
 * 기획서 생성 메시지 템플릿
 * @param {Object} data - 기획서 생성에 필요한 데이터
 * @returns {string} 포맷팅된 메시지
 */
export function createProposalMessage(data) {
  const {
    problemStatement,
    targetUsers,
    coreFeatures,
    optionalFeatures,
    selectedModules,
    platformType,
    estimate
  } = data

  const platformLabel = {
    app: '모바일 앱',
    web: '웹사이트',
    both: '웹 + 앱',
    unsure: '미정'
  }

  return `## 프로젝트 정보
- 플랫폼: ${platformLabel[platformType] || '미정'}
- 예상 비용: ${estimate.priceMin}~${estimate.priceMax}만원
- 예상 기간: ${estimate.timelineWeeks}주

## 서비스 목적
${problemStatement}

## 타겟 사용자
${targetUsers}

## 핵심 기능
${coreFeatures.map(f => `- ${f.name}: ${f.description}`).join('\n')}

## 선택 기능
${optionalFeatures.map(f => `- ${f.name}: ${f.description}`).join('\n')}

## 선택된 모듈
${selectedModules.map(m => `- ${MODULE_CATALOG[m]?.name || m}`).join('\n')}

위 내용을 바탕으로 최종 기획서를 작성해주세요.`
}
